import clsx from 'clsx'
import React from 'react'
import { Logger } from 'wdc-cube'
import { bindUpdate } from 'wdc-cube-react'

import { makeStyles } from 'tss-react/mui'

import DeleteIcon from '@mui/icons-material/DeleteOutlined'
import EditIcon from '@mui/icons-material/Edit'

import {
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    SelectChangeEvent,
    styled,
    Switch,
    Typography
} from '@mui/material'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import { DataGrid, GridActionsCellItem, GridColDef, GridRowId } from '@mui/x-data-grid'
import { classToFComponent, FCClassContext } from 'src/utils/views'
import { FaixaRatingRow, GrupoAnaliseRow } from '../../grupo.types'
import GrupoFormScope from '../grupo_form.scopes'
import RatingFormDataGridToolbar from './rating_form_data_grid_toolbar'

const LOG = Logger.get('GRUPO-FORM-VIEW')

const StyledBox = styled('div')(({ theme }) => ({
    height: 308,
    width: '100%',
    '& .MuiDataGrid-cell--editable': {
        '& .MuiInputBase-root': {
            height: '100%'
        }
    },
    '& .Mui-error': {
        backgroundColor: 'rgb(126,10,15, 0.1)',
        color: '#750f0f',
        ...theme.applyStyles('dark', {
            backgroundColor: 'rgb(126,10,15, 0)',
            color: '#ff4343'
        })
    }
}))

const useStyles = makeStyles()({
    view: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        padding: 10,
        minHeight: 600,
        rowGap: 10
    },
    cnpjInput: {
        flexGrow: 1
    },
    switch: {
        zoom: 0.6
    },
    hbox: {
        display: 'flex',
        flexDirection: 'row'
    },
    vbox: {
        display: 'flex',
        flexDirection: 'column'
    },
    syncButton: {
        alignSelf: 'center'
    }
})

type GrupoFormViewProps = {
    className?: string
    scope: GrupoFormScope
}
class GrupoFormViewClass implements FCClassContext<GrupoFormViewProps> {
    // :: emissors

    readonly emitOnSave = () => {
        return this.scope.onSave().catch(LOG.caught)
    }

    readonly emitOnClose = () => {
        this.scope.onClose().catch(LOG.caught)
    }

    readonly emitOnAddAnalise = () => {
        this.scope.onAddAnalise().catch(LOG.caught)
    }

    #handleChangeAtivoCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
        this.scope.status = e.target.checked ? 'ATIVO' : 'INATIVO'
    }

    #handleTipoChange = (e: SelectChangeEvent) => {
        this.scope.tipo = e.target.value as string
    }

    #handleRemoveFaixaRating = (id: number) => {
        this.scope.onDeleteFaixaRating(id).catch(LOG.caught)
    }

    #handleSaveFaixaRating1 = () => {
        this.scope.onSaveFaixaRating1().catch(LOG.caught)
    }

    #handleEditFaixaRating1 = (id: number) => {
        this.scope.onEditFaixaRating1(id).catch(LOG.caught)
    }

    #handleClearFaixaRating1 = () => {
        this.scope.onClearFaixaRating1().catch(LOG.caught)
    }

    #handleSaveFaixaRating2 = () => {
        this.scope.onSaveFaixaRating2().catch(LOG.caught)
    }

    #handleEditFaixaRating2 = (id: number) => {
        this.scope.onEditFaixaRating2(id).catch(LOG.caught)
    }

    #handleClearFaixaRating2 = () => {
        this.scope.onClearFaixaRating2().catch(LOG.caught)
    }

    #handleDeleteGrupoAnaliseClick = (id: GridRowId) => () => {
        this.scope.idAnaliseSelected = id as number
        this.scope.onDeleteAnalise().catch(LOG.caught)
    }

    // :: Fields
    scope!: GrupoFormScope
    sytles!: ReturnType<typeof useStyles>

    analiseColumns = static_build_analise_columns(this.#handleDeleteGrupoAnaliseClick)
    faixaRating1Columns = static_build_faixa_rating_columns(this.#handleEditFaixaRating1, this.#handleRemoveFaixaRating)
    faixaRating2Columns = static_build_faixa_rating_columns(this.#handleEditFaixaRating2, this.#handleRemoveFaixaRating)

    // :: Methods

    onSyncState({ scope }: GrupoFormViewProps) {
        this.scope = scope
        this.sytles = useStyles()
    }

    render({ className, scope }: GrupoFormViewProps) {
        this.scope = bindUpdate(React, scope)

        const classes = this.sytles.classes

        return (
            <div className={clsx(classes.view, className)}>
                <Paper elevation={3} style={{ padding: 10 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginBottom: '20px'
                        }}
                    >
                        <FormControl>
                            <InputLabel id="tipo-select-label">Tipo</InputLabel>
                            <Select
                                labelId="tipo-select-label"
                                id="tipo-select"
                                defaultValue={scope.tipo}
                                label="Tipo"
                                onChange={this.#handleTipoChange}
                            >
                                <MenuItem value={'Outro'}>Outro</MenuItem>
                                <MenuItem value={'BORDERO'}>Bordero</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.nome}
                            onChange={(e) => (scope.nome = e.target.value)}
                            label="Nome do grupo"
                            variant="standard"
                        />
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.rating1Start}
                            onChange={(e) => (scope.rating1Start = Number(e.target.value ?? 0))}
                            label="Rating 1 Start"
                            variant="standard"
                        />
                        <TextField
                            className={classes.cnpjInput}
                            value={scope.rating2Start}
                            onChange={(e) => (scope.rating2Start = Number(e.target.value ?? 0))}
                            label="Rating 2 Start"
                            variant="standard"
                        />
                        <div className={classes.hbox}>
                            <FormGroup className={classes.vbox}>
                                <FormControlLabel
                                    label={scope.status === 'ATIVO' ? 'Ativo' : 'Inativo'}
                                    control={
                                        <Switch
                                            checked={scope.status === 'ATIVO'}
                                            onChange={this.#handleChangeAtivoCheck}
                                        />
                                    }
                                />
                            </FormGroup>
                        </div>
                    </Box>

                    <Typography variant="h6" sx={{ paddingTop: 4, paddingBottom: 2 }}>
                        Faixas Rating 1
                    </Typography>

                    <StyledBox>
                        <DataGrid
                            rowHeight={40}
                            rows={this.scope.faixasRating1}
                            columns={this.faixaRating1Columns}
                            getRowId={static_getFaixaRatingRowId}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}
                            slots={{ toolbar: RatingFormDataGridToolbar }}
                            slotProps={{
                                toolbar: {
                                    faixaRating: {
                                        sigla: this.scope.editingFaixaRating1Sigla,
                                        descricao: this.scope.editingFaixaRating1Descricao,
                                        faixaInicial: this.scope.editingFaixaRating1FaixaInicial,
                                        faixaFinal: this.scope.editingFaixaRating1FaixaFinal,
                                        mostrarRating: this.scope.editingFaixaRating1MostrarRating
                                    },
                                    onFieldChange: (field, evt) => {
                                        switch (field) {
                                            case 'sigla':
                                                this.scope.editingFaixaRating1Sigla = evt.target.value
                                                break
                                            case 'descricao':
                                                this.scope.editingFaixaRating1Descricao = evt.target.value
                                                break
                                            case 'faixaInicial':
                                                this.scope.editingFaixaRating1FaixaInicial = Number(evt.target.value)
                                                break
                                            case 'faixaFinal':
                                                this.scope.editingFaixaRating1FaixaFinal = Number(evt.target.value)
                                                break
                                            case 'mostrarRating':
                                                this.scope.editingFaixaRating1MostrarRating = (
                                                    evt as React.ChangeEvent<HTMLInputElement>
                                                ).target.checked
                                                break
                                            default:
                                                break
                                        }
                                    },
                                    onSave: this.#handleSaveFaixaRating1,
                                    onClear: this.#handleClearFaixaRating1
                                }
                            }}
                            showToolbar
                        />
                    </StyledBox>

                    <Typography variant="h6" sx={{ paddingTop: 4, paddingBottom: 2 }}>
                        Faixas Rating 2
                    </Typography>
                    <StyledBox>
                        <DataGrid
                            rowHeight={40}
                            rows={this.scope.faixasRating2}
                            columns={this.faixaRating2Columns}
                            getRowId={static_getFaixaRatingRowId}
                            initialState={{
                                pagination: {
                                    paginationModel: {
                                        pageSize: 10
                                    }
                                }
                            }}
                            slots={{ toolbar: RatingFormDataGridToolbar }}
                            slotProps={{
                                toolbar: {
                                    faixaRating: {
                                        sigla: this.scope.editingFaixaRating2Sigla,
                                        descricao: this.scope.editingFaixaRating2Descricao,
                                        faixaInicial: this.scope.editingFaixaRating2FaixaInicial,
                                        faixaFinal: this.scope.editingFaixaRating2FaixaFinal,
                                        mostrarRating: this.scope.editingFaixaRating2MostrarRating
                                    },
                                    onFieldChange: (field, evt) => {
                                        switch (field) {
                                            case 'sigla':
                                                this.scope.editingFaixaRating2Sigla = evt.target.value
                                                break
                                            case 'descricao':
                                                this.scope.editingFaixaRating2Descricao = evt.target.value
                                                break
                                            case 'faixaInicial':
                                                this.scope.editingFaixaRating2FaixaInicial = Number(evt.target.value)
                                                break
                                            case 'faixaFinal':
                                                this.scope.editingFaixaRating2FaixaFinal = Number(evt.target.value)
                                                break
                                            case 'mostrarRating':
                                                this.scope.editingFaixaRating2MostrarRating = (
                                                    evt as React.ChangeEvent<HTMLInputElement>
                                                ).target.checked
                                                break
                                            default:
                                                break
                                        }
                                    },
                                    onSave: this.#handleSaveFaixaRating2,
                                    onClear: this.#handleClearFaixaRating2
                                }
                            }}
                            showToolbar
                        />
                    </StyledBox>
                    {this.scope.idSelected > 0 && (
                        <>
                            <Typography variant="h6" sx={{ paddingTop: 4, paddingBottom: 2 }}>
                                Análises
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    alignItems: 'baseline',
                                    gap: '10px',
                                    marginTop: '10px'
                                }}
                            >
                                <Button variant="outlined" onClick={this.emitOnAddAnalise}>
                                    Adicionar Análise
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    minHeight: 100,
                                    flexDirection: 'row',
                                    alignItems: 'baseline',
                                    gap: '10px',
                                    marginTop: '10px',
                                    marginBottom: '20px'
                                }}
                            >
                                <DataGrid
                                    autoHeight={this.scope.analises.length === 0}
                                    rows={this.scope.analises}
                                    columns={this.analiseColumns}
                                    getRowId={static_getAnaliseRowId}
                                    initialState={{
                                        pagination: {
                                            paginationModel: {
                                                pageSize: 10
                                            }
                                        }
                                    }}
                                />
                            </Box>
                        </>
                    )}

                    <Divider />

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'baseline',
                            gap: '10px',
                            marginTop: '10px'
                        }}
                    >
                        <Button variant="outlined" onClick={this.emitOnSave}>
                            Salvar
                        </Button>
                        <Button variant="outlined" onClick={this.emitOnClose}>
                            Voltar
                        </Button>
                    </Box>
                </Paper>
            </div>
        )
    }
}

export const GrupoFormView = classToFComponent(GrupoFormViewClass, React)
export default GrupoFormView

function static_build_faixa_rating_columns(
    handleEditClick: (idFaixa: number) => void,
    handleDeleteClick: (idFaixa: number) => void
): GridColDef[] {
    return [
        {
            field: 'sigla',
            headerName: 'Sigla',
            width: 80
        },
        {
            field: 'descricao',
            headerName: 'Descrição',
            width: 200
        },
        {
            field: 'faixaInicial',
            headerName: 'Faixa inicial',
            width: 100,
            type: 'number'
        },
        {
            field: 'faixaFinal',
            headerName: 'Faixa final',
            width: 100,
            type: 'number'
        },
        {
            field: 'mostrarRating',
            headerName: 'Mostrar Rating',
            width: 80,
            type: 'boolean'
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Ações',
            width: 80,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'edit' + id}
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => handleEditClick(Number(id))}
                        color="inherit"
                    />,
                    <GridActionsCellItem
                        key={'delete' + id}
                        icon={<DeleteIcon />}
                        label="Delete"
                        className="textError"
                        onClick={() => handleDeleteClick(Number(id))}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_build_analise_columns(handleDeleteClick: (id: GridRowId) => () => void): GridColDef[] {
    return [
        {
            field: 'tipo',
            headerName: 'Tipo',
            width: 200,
            valueFormatter: (value: string) => {
                switch (value) {
                    case 'CEDENTE_SACADO':
                        return 'Cedente/Sacado'
                    case 'CEDENTE':
                        return 'Cedente'
                    case 'SACADO':
                        return 'Sacado'
                    case 'TITULO':
                        return 'Título'
                    case 'NFE':
                        return 'Nfe'
                    case 'LOTE_CNPJ_CPF':
                        return 'Lote CNPJ/CPF'
                    default:
                        return ''
                }
            }
        },
        {
            field: 'nome',
            headerName: 'Nome',
            width: 300
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: 'Actions',
            width: 100,
            cellClassName: 'actions',
            getActions: ({ id }) => {
                return [
                    <GridActionsCellItem
                        key={'select' + id}
                        icon={<DeleteIcon />}
                        label="Add"
                        className="textPrimary"
                        onClick={handleDeleteClick(id)}
                        color="inherit"
                    />
                ]
            }
        }
    ]
}

function static_getAnaliseRowId(row: GrupoAnaliseRow) {
    return row.grupoAnaliseId + ''
}

function static_getFaixaRatingRowId(row: FaixaRatingRow) {
    return row.id + ''
}
